import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, DownloadSimple, Printer, FirstAid, Warning, CurrencyDollar, Funnel, ClipboardText, ChatCircleDots, Archive, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isAdmin, can } from "@/lib/roles";
import { StudentAvatar } from "@/components/StudentAvatar";
import { ArchiveDialog, PermanentDeleteDialog, lifecycleError } from "@/components/LifecycleDialogs";

const PROGRAMS = ["Infant Care", "Toddler Care", "Preschool", "Pre-K", "After School", "Full-Time", "Part-Time"];
const STATUSES = ["Active", "Waitlisted", "Withdrawn", "Graduated", "Archived", "Inactive"];
const ATT = { in: ["Checked in", "bg-emerald-50 text-emerald-700"], out: ["Checked out", "bg-slate-100 text-slate-600"],
              absent: ["Absent", "bg-red-50 text-red-600"], none: ["Not arrived", "bg-amber-50 text-amber-700"] };
const SORTS = { name: "Name", room: "Classroom", enrollment_date: "Enrollment date", last_attendance: "Recent attendance",
                balance: "Past-due balance", completion: "Profile completeness" };

const blank = { first_name: "", last_name: "", birthday: "", gender: "", program: "Preschool", room: "",
                status: "Active", allergies: "", medications: "", doctor: "", notes: "", parent_email: "",
                parent_name: "", parent_phone: "", emergency_contact: "", checkin_code: "" };

const Chip = ({ children, tone = "bg-muted", testid }) => (
  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${tone}`} data-testid={testid}>{children}</span>
);

export default function Students() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [list, setList] = useState(null);
  const [meta, setMeta] = useState({ rooms: [], teachers: [] });
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("Active");
  const [flt, setFlt] = useState({ room: "all", attendance: "all", age_group: "all", teacher: "all", balance: "all", therapy: "all" });
  const [sort, setSort] = useState("name");
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [bulk, setBulk] = useState({ open: false, action: "assign_room", value: "", reason: "" });
  const [archiving, setArchiving] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => Promise.all([api.get("/students/directory"), api.get("/students/meta")])
    .then(([d, m]) => { setList(d.data); setMeta(m.data); })
    .catch((e) => toast.error(apiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("action") === "add") { setF((old) => ({ ...old, room: old.room || meta.rooms[0]?.room || "" })); setOpen(true); }
    if (params.get("status") && (STATUSES.includes(params.get("status")) || params.get("status") === "All")) setTab(params.get("status"));
    if (params.get("room")) setFlt((old) => ({ ...old, room: params.get("room") }));
    if (params.get("filter") === "past_due") setFlt((old) => ({ ...old, balance: "past_due" }));
    if (params.get("filter") === "service_review") setFlt((old) => ({ ...old, therapy: "yes" }));
    if (params.get("filter")) setShowFilters(true);
  }, [params, meta.rooms]);

  const rooms = meta.rooms.map((r) => r.room);
  const counts = useMemo(() => (list || []).reduce((a, s) => ({ ...a, [s.status]: (a[s.status] || 0) + 1 }), {}), [list]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = (list || []).filter((s) => s.status === tab || (tab === "All"));
    if (term)
      rows = rows.filter((s) => [s.name, s.preferred_name, s.parent_name, s.parent_email, s.room, s.student_number, s.parent_phone]
        .some((v) => (v || "").toLowerCase().includes(term)));
    if (flt.room !== "all") rows = rows.filter((s) => s.room === flt.room);
    if (flt.attendance !== "all") rows = rows.filter((s) => s.today_state === flt.attendance);
    if (flt.age_group !== "all") rows = rows.filter((s) => s.age_group === flt.age_group);
    if (flt.teacher !== "all") rows = rows.filter((s) => s.primary_teacher_email === flt.teacher);
    if (flt.balance === "past_due") rows = rows.filter((s) => s.past_due);
    if (flt.balance === "owing") rows = rows.filter((s) => s.balance > 0);
    if (flt.balance === "clear") rows = rows.filter((s) => s.balance === 0);
    if (flt.therapy !== "all") rows = rows.filter((s) => s.therapy === (flt.therapy === "yes"));
    if (params.get("filter") === "incomplete") rows = rows.filter((s) => Number(s.completion || 0) < 100);
    if (params.get("filter") === "allergy") rows = rows.filter((s) => s.allergies && !["none", ""].includes(s.allergies.toLowerCase()));
    const dir = sort === "balance" || sort === "completion" || sort === "last_attendance" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a[sort] ?? "", bv = b[sort] ?? "";
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });
  }, [list, q, tab, flt, sort, params]);

  const save = async () => {
    try {
      await api.post("/students", f);
      toast.success("Student added");
      setOpen(false);
      setF(blank);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const restore = async (s) => {
    try {
      const { data } = await api.post(`/students/${s.id}/restore`, { reason: "Restored from the student archive" });
      toast.success(data.message || "Student restored");
      load();
    } catch (e) { toast.error(lifecycleError(e.response?.data?.detail)); }
  };

  const runBulk = async () => {
    if (!bulk.value) return toast.error("Choose a value");
    try {
      const { data } = await api.post("/students/bulk", { ids: selected, action: bulk.action, value: bulk.value, reason: bulk.reason });
      toast.success(`Updated ${data.updated} student${data.updated === 1 ? "" : "s"}`);
      setBulk({ ...bulk, open: false });
      setSelected([]);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/students/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = "students.csv"; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const messageSelected = () => {
    const emails = [...new Set(filtered.filter((s) => selected.includes(s.id)).map((s) => s.parent_email).filter(Boolean))];
    nav(`/admin/messages?to=${encodeURIComponent(emails.join(","))}`);
  };

  return (
    <div data-testid="students-page">
      <div className="flex flex-wrap justify-between items-end gap-4 print:hidden">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Records</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Students</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/admissions"><Button variant="outline" className="rounded-full h-12 font-bold" data-testid="to-admissions-btn">
            <ClipboardText size={18} className="mr-2" /> Admissions</Button></Link>
          {can(user, "students", "export") && (
            <>
              <Button variant="outline" onClick={exportCsv} className="rounded-full h-12 font-bold" data-testid="export-students-btn">
                <DownloadSimple size={18} className="mr-2" /> Export</Button>
              <Button variant="outline" onClick={() => window.print()} className="rounded-full h-12 font-bold" data-testid="print-students-btn">
                <Printer size={18} className="mr-2" /> Print</Button>
            </>
          )}
          {can(user, "students", "create") && (
            <Button onClick={() => { setF({ ...blank, room: rooms[0] || "" }); setOpen(true); }} className="rounded-full h-12 px-7 bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="add-student-btn">
              <Plus size={18} className="mr-2" /> Add student</Button>
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2 print:hidden" data-testid="status-tabs">
        {[...STATUSES, "All"].map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`status-tab-${s.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-sm font-bold border ${tab === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-border hover:border-blue-300"}`}
                  style={{ transition: "background-color .2s ease, border-color .2s ease" }}>
            {s} <span className="opacity-60">{s === "All" ? (list || []).length : counts[s] || 0}</span>
          </button>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-3 items-center print:hidden">
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-12 rounded-xl max-w-md"
               placeholder="Search name, parent, classroom, student ID or phone…" data-testid="student-search" />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-12 rounded-xl w-56" data-testid="student-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>Sort: {v}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="h-12 rounded-xl font-bold" data-testid="toggle-filters-btn">
          <Funnel size={18} className="mr-2" /> Filters</Button>
        <p className="text-sm text-muted-foreground font-semibold ml-auto" data-testid="student-count">{filtered.length} shown</p>
      </div>

      {showFilters && (
        <div className="mt-4 grid sm:grid-cols-3 xl:grid-cols-6 gap-3 bg-white rounded-2xl border border-border p-4 print:hidden" data-testid="filters-panel">
          {[["room", "Classroom", ["all", ...rooms]], ["attendance", "Attendance today", ["all", "in", "out", "absent", "none"]],
            ["age_group", "Age group", ["all", "Infant", "Toddler", "Preschool", "Pre-K", "School Age"]],
            ["teacher", "Teacher", ["all", ...meta.teachers.map((t) => t.email)]],
            ["balance", "Balance", ["all", "past_due", "owing", "clear"]], ["therapy", "Services", ["all", "yes", "no"]]].map(([k, label, opts]) => (
            <div key={k}>
              <Label className="text-xs font-bold uppercase tracking-wider">{label}</Label>
              <Select value={flt[k]} onValueChange={(v) => setFlt({ ...flt, [k]: v })}>
                <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid={`filter-${k}`}><SelectValue /></SelectTrigger>
                <SelectContent>{opts.map((o) => (
                  <SelectItem key={o} value={o}>{o === "all" ? "Any" : k === "teacher" ? (meta.teachers.find((t) => t.email === o)?.name || o)
                    : k === "attendance" ? ATT[o][0] : o.replace("_", " ")}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      {selected.length > 0 && isAdmin(user) && (
        <div className="mt-4 flex flex-wrap gap-2 items-center bg-[#0F172A] text-white rounded-2xl px-5 py-3 print:hidden" data-testid="bulk-bar">
          <span className="font-bold text-sm">{selected.length} selected</span>
          <Button size="sm" className="rounded-full bg-[#3B82F6] font-bold" onClick={() => setBulk({ open: true, action: "assign_room", value: "", reason: "" })} data-testid="bulk-assign-room-btn">Assign classroom</Button>
          <Button size="sm" className="rounded-full bg-[#FBBF24] text-[#0F172A] font-bold" onClick={() => setBulk({ open: true, action: "set_status", value: "", reason: "" })} data-testid="bulk-status-btn">Set status</Button>
          <Button size="sm" variant="outline" className="rounded-full font-bold text-white border-white/40 hover:bg-white/10" onClick={messageSelected} data-testid="bulk-message-btn">
            <ChatCircleDots size={16} className="mr-1" /> Message families</Button>
          <Button size="sm" variant="ghost" className="rounded-full text-white/70 ml-auto" onClick={() => setSelected([])} data-testid="bulk-clear-btn">Clear</Button>
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="students-list">
        {list === null && <p className="text-muted-foreground">Loading…</p>}
        {list !== null && filtered.length === 0 && <p className="text-muted-foreground" data-testid="students-empty">No students match.</p>}
        {filtered.map((s) => (
          <div key={s.id} className={`bg-white rounded-2xl border p-5 relative ${selected.includes(s.id) ? "border-[#3B82F6]" : "border-border"}`}
               data-testid={`student-card-${s.id}`}>
            {isAdmin(user) && (
              <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)}
                     className="absolute top-4 right-4 h-4 w-4 print:hidden" data-testid={`select-${s.id}`} aria-label={`Select ${s.name}`} />
            )}
            <Link to={`/admin/students/${s.id}`} className="flex gap-4 items-start" data-testid={`open-student-${s.id}`}>
              <StudentAvatar student={s} size="md" />
              <div className="min-w-0">
                <p className="font-display text-lg font-semibold leading-tight truncate">{s.name}</p>
                {s.preferred_name && <p className="text-xs text-muted-foreground font-semibold">“{s.preferred_name}”</p>}
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#3B82F6] mt-1">{s.student_number} · {s.age || "—"}</p>
              </div>
            </Link>
            <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
              {[["Classroom", s.room], ["Teacher", s.primary_teacher || "—"], ["Birthday", s.birthday || "—"], ["Status", s.status]].map(([k, v]) => (
                <div key={k}><dt className="text-xs text-muted-foreground font-semibold">{k}</dt><dd className="font-semibold truncate">{v}</dd></div>
              ))}
            </dl>
            <div className="mt-4 flex flex-wrap gap-1.5">
              <Chip tone={ATT[s.today_state][1]} testid={`today-${s.id}`}>{ATT[s.today_state][0]}</Chip>
              {s.medical_alert && <Chip tone="bg-red-50 text-red-600" testid={`medical-${s.id}`}><FirstAid size={12} className="inline mr-1" />Medical alert</Chip>}
              {s.pickup_alert && <Chip tone="bg-orange-50 text-orange-700" testid={`pickup-${s.id}`}><Warning size={12} className="inline mr-1" />Pickup restriction</Chip>}
              {s.balance > 0 && <Chip tone={s.past_due ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"} testid={`balance-${s.id}`}>
                <CurrencyDollar size={12} className="inline" />{money(s.balance)}{s.past_due ? " past due" : ""}</Chip>}
              {s.balance === 0 && <Chip tone="bg-emerald-50 text-emerald-700">Paid up</Chip>}
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs font-bold text-muted-foreground"><span>Profile</span><span>{s.completion}%</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-[#10B981]" style={{ width: `${s.completion}%` }} /></div>
            </div>
            {isAdmin(user) && (
              <div className="mt-4 flex flex-wrap gap-2 print:hidden">
                {s.status !== "Archived" ? (
                  <Button size="sm" variant="outline" onClick={() => setArchiving(s)}
                          className="rounded-full font-bold text-amber-700" data-testid={`archive-student-${s.id}`}>
                    <Archive size={15} className="mr-1" /> Archive
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => restore(s)}
                            className="rounded-full font-bold text-emerald-700" data-testid={`restore-student-${s.id}`}>
                      <ArrowCounterClockwise size={15} className="mr-1" /> Restore
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDeleting(s)}
                            className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-student-${s.id}`}>
                      <Trash size={15} className="mr-1" /> Delete
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="student-dialog">
          <DialogHeader><DialogTitle className="font-display">Add student</DialogTitle>
            <DialogDescription>Required fields are marked. You can complete the rest from the profile.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["first_name", "First name", "text", true], ["last_name", "Last name", "text", true], ["birthday", "Birthday", "date"],
              ["gender", "Gender"], ["allergies", "Allergies"], ["medications", "Medications"], ["doctor", "Doctor"],
              ["checkin_code", "Check-in code (auto if blank)"], ["parent_name", "Parent name"], ["parent_email", "Parent email", "email"],
              ["parent_phone", "Parent phone"], ["emergency_contact", "Emergency contact"]].map(([k, label, type, req]) => (
              <div key={k}>
                <Label className="text-sm font-bold">{label}{req && <span className="text-[#EF4444]"> *</span>}</Label>
                <Input type={type || "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid={`student-field-${k}`} />
              </div>
            ))}
            {[["program", PROGRAMS], ["room", rooms], ["status", STATUSES]].map(([k, opts]) => (
              <div key={k}>
                <Label className="text-sm font-bold capitalize">{k}</Label>
                <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid={`student-field-${k}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={!f.first_name || !f.last_name} className="mt-4 w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="student-save-btn">Add student</Button>
        </DialogContent>
      </Dialog>

      <ArchiveDialog open={Boolean(archiving)} onOpenChange={(o) => !o && setArchiving(null)}
                     kind="students" record={archiving} onDone={load} />
      <PermanentDeleteDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}
                             kind="students" record={deleting} onDone={load} />

      <Dialog open={bulk.open} onOpenChange={(o) => setBulk({ ...bulk, open: o })}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="bulk-dialog">
          <DialogHeader><DialogTitle className="font-display">{bulk.action === "assign_room" ? "Assign classroom" : "Set status"} · {selected.length} students</DialogTitle>
            <DialogDescription>This change is recorded in the audit log.</DialogDescription></DialogHeader>
          <Select value={bulk.value || "none"} onValueChange={(v) => setBulk({ ...bulk, value: v === "none" ? "" : v })}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="bulk-value"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Choose…</SelectItem>
              {(bulk.action === "assign_room" ? rooms : STATUSES).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={bulk.reason} onChange={(e) => setBulk({ ...bulk, reason: e.target.value })} placeholder="Reason (optional)" className="h-11 rounded-xl" data-testid="bulk-reason" />
          <Button onClick={runBulk} className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="bulk-apply-btn">Apply</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
